/**
 * Statuswoordenlijst voor taken en projecten — één bron (P2, brede analyse 5 sep, FU-09/17).
 *
 * De Tasks-tabel mengt in `PrioNr` prioriteiten, kalender-types en eindstaten (CLAUDE.md
 * "Taken (Tasks) Statussen"). Elk scherm had zijn eigen lijstje en die weken af: notificaties
 * misten 'Gearchiveerd', Capaciteit telde gearchiveerde taken mee, het PL-dashboard toonde ze
 * als deadline. De engine heeft dezelfde lijst in `core/taken.py`; een test bewaakt dat ze
 * gelijk blijven.
 */
import { PRIO_COLORS, TYPE_COLORS } from './taakKleuren';

/** Eindstaten van een taak: telt als klaar, komt nergens meer als werk terug. */
export const AFGERONDE_STATUSSEN = ['Afgerond', 'Opgestuurd', 'Gearchiveerd'];
/** Wacht-/blokkeerstaten: geen werk nú, maar ook niet klaar. */
export const WACHT_STATUSSEN = ['Wacht op feedback', 'Nog niet van toepassing'];
/** Kalenderblokken: staan in dezelfde tabel maar zijn geen taak (meetings, vrije dagen, shoots). */
export const AGENDA_TYPES = ['Afspraak', 'Vrij/intern', 'Vrije dag', 'Draaidag', 'Vanallesuurtje'];
/**
 * Dagstaten: een draaidag of vrije dag is een dag, geen werk dat af moet. Voorbij = voorbij,
 * nooit "achterstallig" of "te laat" en hoeft niet afgevinkt te worden (Joris, 3 nov 2026).
 * Bewust zonder 'Vrij/intern': dat is intern werk.
 */
export const DAGSTAAT_TYPES = ['Draaidag', 'Vrije dag'];
/** Prioriteiten van een actieve taak. */
export const PRIO_STATUSSEN = ['Deadline', 'Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Geen prio'];
/** Alles wat niet als productiewerk telt (werkdruk, capaciteit, to-do-lijsten). */
export const NIET_PRODUCTIE = [...AGENDA_TYPES, ...AFGERONDE_STATUSSEN];

export const AFGEROND_SET = new Set(AFGERONDE_STATUSSEN);
export const NIET_PRODUCTIE_SET = new Set(NIET_PRODUCTIE);

const veld = (t, ...namen) => {
  for (const n of namen) {
    const v = t?.[n] ?? t?.fields?.[n];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
};

/** Klaar? Werkt op Airtable-records ({fields:{PrioNr,Afgerond}}) én op platte DTO's ({prio|status, afgerond|isComplete}). */
export function isTaakAfgerond(t) {
  if (!t) return false;
  if (veld(t, 'Afgerond', 'afgerond', 'isComplete', 'is_completed') === true) return true;
  return AFGEROND_SET.has(veld(t, 'PrioNr', 'prio', 'prio_nr', 'status', 'priority'));
}

export const DAGSTAAT_SET = new Set(DAGSTAAT_TYPES);

/** Draaidag of vrije dag? Op type of status (PrioNr); records én platte DTO's. */
export function isDagstaatTaak(t) {
  if (!t) return false;
  return DAGSTAAT_SET.has(veld(t, 'Type', 'type', 'task_type'))
    || DAGSTAAT_SET.has(veld(t, 'PrioNr', 'prio', 'prio_nr', 'status', 'priority'));
}

/** Productiewerk? (geen kalenderblok, niet klaar) */
export function isProductieTaak(t) {
  if (isTaakAfgerond(t)) return false;
  return !NIET_PRODUCTIE_SET.has(veld(t, 'PrioNr', 'prio', 'prio_nr', 'status', 'priority'))
    && !NIET_PRODUCTIE_SET.has(veld(t, 'Type', 'type', 'task_type'));
}

/**
 * Projectstatus: wat de waarde betekent en wie 'm zet. Precies dit onderscheid was het
 * incident van juli (Gripp-gearchiveerd = gefactureerd → Airtable-Afgerond → evaluatiemail).
 */
export const PROJECT_STATUS_UITLEG = {
  'Lead': 'Kans, nog geen offerte',
  'Offerte': 'Offerte verstuurd, nog niet geaccepteerd',
  'In progress': 'Geaccepteerd en in uitvoering (ook Te factureren)',
  'On Hold': 'Tijdelijk stil — volgt Gripp-fase On Hold',
  'Afgerond': 'Gefactureerd — volgt automatisch uit Gripp (gearchiveerd + factuur) en stuurt de evaluatiemail naar de klant',
  'Gearchiveerd': 'Op tape gezet — handmatige stap ná Afgerond, de sync raakt dit niet meer aan',
  'Geannuleerd': 'Niet doorgegaan of offerte afgewezen — geen factuur',
  'Vernietigd': 'Materiaal vernietigd — handmatige eindstaat',
  'Geen planning nodig': 'Geen taken/planning (bv. strippenkaart of losse levering)',
};

export { PRIO_COLORS, TYPE_COLORS };
